import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Container,
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Receipt,
  Visibility,
  PointOfSale,
  Edit,
  Payment,
  Cancel,
  MenuBook,
} from "@mui/icons-material";
import { salesAPI } from "../../services/API";
import { useSnackbar } from "notistack";

const ActionDialogs = ({
  actionDialog,
  setActionDialog,
  handleConfirmCancel,
  paymentData,
  setPaymentData,
  handleConfirmPaymentUpdate,
}) => {
  return (
    <>
      {/* Cancel Sale Dialog */}
      <Dialog
        open={actionDialog.open && actionDialog.type === "cancel"}
        onClose={() => setActionDialog({ open: false, type: "", sale: null })}
      >
        <DialogTitle sx={{ fontSize: "1.5rem" }}>Cancel Sale</DialogTitle>
        <DialogContent>
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              fontSize: "1.1rem",
              "& .MuiAlert-message": { fontSize: "1.1rem" },
            }}
          >
            This action cannot be undone. Stock will be restored.
          </Alert>
          <Typography variant="h6" sx={{ fontSize: "1.1rem" }}>
            Are you sure you want to cancel invoice #
            {actionDialog.sale?.invoiceNumber}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setActionDialog({ open: false, type: "", sale: null })
            }
            sx={{ fontSize: "1.1rem" }}
          >
            No, Keep It
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancel}
            sx={{ fontSize: "1.1rem" }}
          >
            Yes, Cancel Sale
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Payment Dialog */}
      <Dialog
        open={actionDialog.open && actionDialog.type === "payment"}
        onClose={() => setActionDialog({ open: false, type: "", sale: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1.5rem" }}>Update Payment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontSize: "1.1rem" }}
              >
                Invoice #: {actionDialog.sale?.invoiceNumber}
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ fontSize: "1.3rem" }}>
                Grand Total: ₹{actionDialog.sale?.grandTotal?.toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Payment Method"
                value={paymentData?.paymentMethod}
                onChange={(e) =>
                  setPaymentData((prev) => ({
                    ...prev,
                    paymentMethod: e.target.value,
                  }))
                }
                sx={{ fontSize: "1.1rem" }}
                InputProps={{ sx: { fontSize: "1.1rem" } }}
                InputLabelProps={{ sx: { fontSize: "1.1rem" } }}
              >
                <MenuItem value="cash" sx={{ fontSize: "1.1rem" }}>
                  Cash
                </MenuItem>
                <MenuItem value="card" sx={{ fontSize: "1.1rem" }}>
                  Card
                </MenuItem>
                <MenuItem value="upi" sx={{ fontSize: "1.1rem" }}>
                  UPI
                </MenuItem>
                <MenuItem value="insurance" sx={{ fontSize: "1.1rem" }}>
                  Insurance
                </MenuItem>
                <MenuItem value="credit" sx={{ fontSize: "1.1rem" }}>
                  Credit
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Paid Amount"
                value={paymentData?.paidAmount}
                onChange={(e) =>
                  setPaymentData((prev) => ({
                    ...prev,
                    paidAmount: parseFloat(e.target.value) || 0,
                  }))
                }
                inputProps={{ min: 0, step: 0.01, style: { fontSize: "1.1rem" } }}
                sx={{ fontSize: "1.1rem" }}
                InputLabelProps={{ sx: { fontSize: "1.1rem" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert
                severity="info"
                sx={{
                  fontSize: "1.1rem",
                  "& .MuiAlert-message": { fontSize: "1.1rem" },
                }}
              >
                Due Amount: ₹
                {Math.max(
                  0,
                  (actionDialog.sale?.grandTotal || 0) - paymentData?.paidAmount
                ).toFixed(2)}
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setActionDialog({ open: false, type: "", sale: null })}
            sx={{ fontSize: "1.1rem" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmPaymentUpdate}
            sx={{ fontSize: "1.1rem" }}
          >
            Update Payment
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const Sales = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: "", // 'edit', 'cancel', 'payment'
    sale: null,
  });
  const [paymentData, setPaymentData] = useState({
    paidAmount: 0,
    paymentMethod: "cash",
  });

  useEffect(() => {
    fetchSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSales = async () => {
    try {
      const response = await salesAPI.getAll();
      // Expecting response.data.data.salesInvoices
      setSales(response.data.data.salesInvoices || []);
    } catch (error) {
      enqueueSnackbar("Error fetching sales", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, sale) => {
    setAnchorEl(event.currentTarget);
    setSelectedSale(sale);
  };

  const handleEditSale = () => {
    if (!selectedSale) return;
    navigate(`/hospital/pharmacy/sales/${selectedSale._id}/edit`);
    handleMenuClose();
  };

  const handleCancelSale = () => {
    if (!selectedSale) return;
    setActionDialog({
      open: true,
      type: "cancel",
      sale: selectedSale,
    });
    handleMenuClose();
  };

  const handleUpdatePayment = () => {
    if (!selectedSale) return;
    setPaymentData({
      paidAmount: selectedSale.paidAmount || 0,
      paymentMethod: selectedSale.paymentMethod || "cash",
    });
    setActionDialog({
      open: true,
      type: "payment",
      sale: selectedSale,
    });
    handleMenuClose();
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSale(null);
  };

  const handleViewDetails = () => {
    if (!selectedSale) return;
    navigate(`/hospital/pharmacy/sales/${selectedSale._id}`);
    handleMenuClose();
  };

  // Add print function
  const handlePrintInvoice = async () => {
    if (!selectedSale) {
      handleMenuClose();
      return;
    }

    try {
      const response = await salesAPI.get(selectedSale._id);
      const invoice = response.data.data.salesInvoice || response.data.data.salesInvoice; // tolerate different shapes

      const patient = invoice.patient || null;

      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
      <html>
        <head>
          <title>Invoice #${invoice.invoiceNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 16px; color: #222 }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .details { margin: 20px 0; }
            .table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 14px; }
            .table th, .table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            .table th { background-color: #f5f5f5; font-size: 14px; }
            .totals { float: right; margin-top: 20px; font-size: 16px; text-align: right; }
            .footer { margin-top: 50px; border-top: 1px solid #333; padding-top: 10px; }
            .small { font-size: 13px; color: #555; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1 style="font-size: 28px; margin: 0">PHARMACY INVOICE</h1>
            <h2 style="font-size: 22px; margin: 0">#${invoice.invoiceNumber}</h2>
          </div>
          
          <div class="details">
            <p style="font-size: 16px; margin: 6px 0;"><strong>Date:</strong> ${new Date(
              invoice.saleDate
            ).toLocaleDateString()} ${new Date(invoice.saleDate).toLocaleTimeString()}</p>
            ${
              patient
                ? `<p style="font-size: 16px; margin: 6px 0;">
                    <strong>Patient:</strong> ${patient.first_name || ""} ${patient.last_name || ""} <br>
                    <span class="small"><strong>UHID:</strong> ${patient.uhid || "-"} &nbsp; | &nbsp; <strong>OP NO:</strong> ${patient.op_no || "-"} &nbsp; | &nbsp; <strong>IPD NO:</strong> ${patient.ipd_no || "-"}</span>
                  </p>`
                : `<p style="font-size: 16px; margin: 6px 0;"><strong>Patient:</strong> Walk-in Customer</p>`
            }
            <p style="font-size: 16px; margin: 6px 0;"><strong>Payment Status:</strong> ${invoice.paymentStatus}</p>
            <p style="font-size: 16px; margin: 6px 0;"><strong>Payment Method:</strong> ${invoice.paymentMethod}</p>
          </div>
          
          <table class="table">
            <thead>
              <tr>
                <th>Medicine</th>
                <th>Batch</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Disc.</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${invoice.items
                .map(
                  (item) => `
                <tr>
                  <td style="font-size: 14px;">${item.medicine?.name || item.medicine}</td>
                  <td style="font-size: 14px;">${item.batchNumber || ""}</td>
                  <td style="font-size: 14px;">${item.quantity}</td>
                  <td style="font-size: 14px;">₹${item.sellingPrice?.toFixed ? item.sellingPrice.toFixed(2) : item.sellingPrice}</td>
                  <td style="font-size: 14px;">${item.discount || 0}%</td>
                  <td style="font-size: 14px;">₹${item.totalAmount?.toFixed ? item.totalAmount.toFixed(2) : item.totalAmount}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          
          <div class="totals">
            <p style="font-size: 16px; margin: 4px 0;"><strong>Subtotal:</strong> ₹${invoice.subtotal?.toFixed(2)}</p>
            <p style="font-size: 16px; margin: 4px 0;"><strong>Tax:</strong> ₹${invoice.taxAmount?.toFixed(2)}</p>
            <p style="font-size: 18px; margin: 6px 0;"><strong>Total:</strong> ₹${invoice.grandTotal?.toFixed(2)}</p>
            <p style="font-size: 16px; margin: 4px 0;"><strong>Paid:</strong> ₹${invoice.paidAmount?.toFixed(2)}</p>
            <p style="font-size: 16px; margin: 4px 0;"><strong>Due:</strong> ₹${invoice.dueAmount?.toFixed(2)}</p>
          </div>
          
          <div class="footer">
            <p style="font-size: 16px; margin: 6px 0;">Thank you for your business!</p>
          </div>
        </body>
      </html>
    `);
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      enqueueSnackbar("Error generating invoice", { variant: "error" });
      console.error(error);
    }
    handleMenuClose();
  };

  const handleConfirmCancel = async () => {
    try {
      if (!actionDialog.sale) return;
      await salesAPI.cancel(actionDialog.sale._id);
      enqueueSnackbar("Sale cancelled successfully", { variant: "success" });
      fetchSales(); // Refresh list
      setActionDialog({ open: false, type: "", sale: null });
    } catch (error) {
      enqueueSnackbar("Error cancelling sale", { variant: "error" });
      console.error(error);
    }
  };

  const handleConfirmPaymentUpdate = async () => {
    try {
      if (!actionDialog.sale) return;
      const { grandTotal } = actionDialog.sale;
      const paidAmount = paymentData.paidAmount || 0;
      const dueAmount = Math.max(0, grandTotal - paidAmount);

      let paymentStatus = "pending";
      if (dueAmount === 0 && paidAmount > 0) {
        paymentStatus = "paid";
      } else if (dueAmount > 0 && dueAmount < grandTotal) {
        paymentStatus = "partially_paid";
      }

      await salesAPI.updatePayment(actionDialog.sale._id, {
        paidAmount,
        paymentMethod: paymentData.paymentMethod,
        paymentStatus,
      });

      enqueueSnackbar("Payment updated successfully", { variant: "success" });
      fetchSales(); // Refresh list
      setActionDialog({ open: false, type: "", sale: null });
    } catch (error) {
      enqueueSnackbar("Error updating payment", { variant: "error" });
      console.error(error);
    }
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      paid: "success",
      partially_paid: "warning",
      pending: "error",
      cancelled: "default",
    };
    return colors[status] || "default";
  };

  // Filter by invoice number or patient name (snake_case)
  const filteredSales = sales.filter((sale) => {
    const invoiceMatch = sale.invoiceNumber
      ? sale.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase())
      : false;

    const patientName = sale.patient
      ? `${sale.patient.first_name || ""} ${sale.patient.last_name || ""}`.toLowerCase()
      : "";

    const patientMatch = patientName.includes(searchTerm.toLowerCase());

    return invoiceMatch || patientMatch;
  });

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)" }}>
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: "2rem", md: "2.5rem" } }}>
            Sales History
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add sx={{ fontSize: 24 }} />}
            onClick={() => navigate("/hospital/pharmacy/sales/new")}
            sx={{
              borderRadius: 2,
              fontSize: "1.1rem",
              py: 1.5,
              px: 3,
            }}
          >
            New Sale
          </Button>
        </Box>

        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
          <CardContent sx={{ py: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by invoice number or patient name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ fontSize: "1.1rem" }}
              InputProps={{
                sx: { fontSize: "1.1rem" },
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 28 }} />
                  </InputAdornment>
                ),
              }}
            />
          </CardContent>
        </Card>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, textAlign: "center", transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-4px)" } }}>
              <CardContent sx={{ py: 3 }}>
                <PointOfSale color="primary" sx={{ fontSize: 50, mb: 2 }} />
                <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: "2rem", md: "2.5rem" } }}>
                  {sales.length}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: "1.1rem" }}>
                  Total Sales
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, textAlign: "center", transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-4px)" } }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h3" fontWeight="bold" color="success.main" sx={{ fontSize: { xs: "2rem", md: "2.5rem" } }}>
                  {sales.filter((s) => s.paymentStatus === "paid").length}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: "1.1rem" }}>
                  Paid Invoices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, textAlign: "center", transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-4px)" } }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h3" fontWeight="bold" color="warning.main" sx={{ fontSize: { xs: "2rem", md: "2.5rem" } }}>
                  {sales.filter((s) => s.paymentStatus === "partially_paid").length}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: "1.1rem" }}>
                  Partial Payments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, textAlign: "center", transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-4px)" } }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h3" fontWeight="bold" color="error.main" sx={{ fontSize: { xs: "2rem", md: "2.5rem" } }}>
                  {sales.filter((s) => s.paymentStatus === "pending").length}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: "1.1rem" }}>
                  Pending Payments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ py: 3 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: "1.5rem", mb: 3 }}>
              Recent Sales
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: "1.1rem", fontWeight: "bold", py: 2 }}>Invoice #</TableCell>
                    <TableCell sx={{ fontSize: "1.1rem", fontWeight: "bold", py: 2 }}>Patient</TableCell>
                    <TableCell sx={{ fontSize: "1.1rem", fontWeight: "bold", py: 2 }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontSize: "1.1rem", fontWeight: "bold", py: 2 }}>Amount</TableCell>
                    <TableCell align="center" sx={{ fontSize: "1.1rem", fontWeight: "bold", py: 2 }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontSize: "1.1rem", fontWeight: "bold", py: 2 }}>Payment Status</TableCell>
                    <TableCell align="center" sx={{ fontSize: "1.1rem", fontWeight: "bold", py: 2 }}>Payment Method</TableCell>
                    <TableCell align="center" sx={{ fontSize: "1.1rem", fontWeight: "bold", py: 2 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSales.map((sale) => (
                    <TableRow key={sale._id} hover sx={{ "&:hover": { backgroundColor: "#fafafa" } }}>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1.1rem" }}>
                          {sale.invoiceNumber}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {sale.patient ? (
                          <Box>
                            <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
                              {sale.patient.first_name} {sale.patient.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.95rem" }}>
                              UHID: {sale.patient.uhid || "-"} • OP: {sale.patient.op_no || "-"} {sale.patient.ipd_no ? `• IPD: ${sale.patient.ipd_no}` : ""}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1.1rem" }}>
                            Walk-in Customer
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
                          {sale.saleDate ? new Date(sale.saleDate).toLocaleDateString() : ""}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1rem" }}>
                          {sale.saleDate ? new Date(sale.saleDate).toLocaleTimeString() : ""}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1.1rem" }}>
                          ₹{sale.grandTotal?.toFixed ? sale.grandTotal.toFixed(2) : sale.grandTotal}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        <Chip
                          label={sale.status}
                          size="medium"
                          color={sale.status === "completed" ? "success" : "info"}
                          sx={{ fontSize: "1rem" }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        <Chip
                          label={sale.paymentStatus}
                          size="medium"
                          color={getPaymentStatusColor(sale.paymentStatus)}
                          sx={{ fontSize: "1rem" }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
                          {sale.paymentMethod}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        <IconButton size="large" onClick={(e) => handleMenuOpen(e, sale)} sx={{ fontSize: "1.5rem" }}>
                          <MoreVert fontSize="inherit" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {sales.length === 0 && (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <MenuBook sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                <Typography variant="h4" color="text.secondary" gutterBottom sx={{ fontSize: "1.5rem", mb: 2 }}>
                  No sales found
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: "1.1rem", mb: 3 }}>
                  Start by creating a new sale
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add sx={{ fontSize: 24 }} />}
                  onClick={() => navigate("/hospital/pharmacy/sales/new")}
                  sx={{
                    fontSize: "1.1rem",
                    py: 1.5,
                    px: 3,
                  }}
                >
                  New Sale
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        <ActionDialogs
          actionDialog={actionDialog}
          setActionDialog={setActionDialog}
          handleConfirmCancel={handleConfirmCancel}
          paymentData={paymentData}
          setPaymentData={setPaymentData}
          handleConfirmPaymentUpdate={handleConfirmPaymentUpdate}
        />

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              minWidth: 200,
              "& .MuiMenuItem-root": {
                fontSize: "1.1rem",
                py: 1.5,
              },
            },
          }}
        >
          <MenuItem onClick={handleViewDetails}>
            <Visibility sx={{ mr: 2, fontSize: 24 }} />
            View Details
          </MenuItem>
          <MenuItem onClick={handleEditSale}>
            <Edit sx={{ mr: 2, fontSize: 24 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleCancelSale}>
            <Cancel sx={{ mr: 2, fontSize: 24 }} />
            Cancel Sale
          </MenuItem>
          <MenuItem onClick={handleUpdatePayment}>
            <Payment sx={{ mr: 2, fontSize: 24 }} />
            Update Payment
          </MenuItem>
          <MenuItem onClick={handlePrintInvoice}>
            <Receipt sx={{ mr: 2, fontSize: 24 }} />
            Print Invoice
          </MenuItem>
        </Menu>
      </Container>
    </Box>
  );
};

export default Sales;
